import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  FileDown, FileSpreadsheet, Loader2, Save, TrendingUp, TrendingDown, Minus,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { RagBadge } from "@/components/Rag";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const money = (v) =>
  typeof v === "number" && v
    ? `£${v.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`
    : "—";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const Section = ({ title, note, children, testId }) => (
  <section className="panel mt-6 p-5" data-testid={testId}>
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h3 className="font-display text-base font-semibold text-slate-800">{title}</h3>
      {note && <p className="text-xs text-slate-500">{note}</p>}
    </div>
    <div className="mt-4">{children}</div>
  </section>
);

const Table = ({ head, rows, empty = "Nothing to report.", testId }) =>
  !rows.length ? (
    <p className="text-sm text-slate-500">{empty}</p>
  ) : (
    <div className="overflow-x-auto">
      <table data-testid={testId} className="w-full min-w-[640px] text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-400">
            {head.map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, i) => (
            <tr key={i} className="border-t border-slate-100 text-slate-700">
              {cells.map((c, j) => <td key={j} className="px-3 py-2.5">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

export default function ClientReport() {
  const { id } = useParams();
  const [period, setPeriod] = useState({ start: daysAgo(30), end: today() });
  const [data, setData] = useState(null);
  const [commentary, setCommentary] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(
      `/clients/${id}/report?period_start=${period.start}&period_end=${period.end}`,
    );
    setData(data);
    setCommentary(data.commentary || "");
  }, [id, period.start, period.end]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e.response?.data?.detail))); }, [load]);

  const saveCommentary = async () => {
    setBusy("commentary");
    try {
      await api.put(`/clients/${id}/commentary`, {
        period_start: period.start, period_end: period.end, commentary,
      });
      toast.success("Commentary saved for this period");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const res = await api.get(
        `/clients/${id}/report.${fmt}?period_start=${period.start}&period_end=${period.end}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `client-report-${(data?.client?.name || "client").toLowerCase().replace(/\s+/g, "-")}-${period.end}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Pack issued. Movement will now be measured from this one.");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not produce the pack");
    } finally {
      setBusy(null);
    }
  };

  if (!data) {
    return (
      <Shell testId="client-report-page" title="Client report" subtitle="Loading">
        <div className="flex items-center gap-2 p-8 text-sm text-slate-500">
          <Loader2 size={15} className="animate-spin" /> Building the pack…
        </div>
      </Shell>
    );
  }

  const h = data.header;
  const e = data.exposure;
  const w = data.waiting_on_client;

  return (
    <Shell
      testId="client-report-page"
      title={`${data.client.name} — client report`}
      subtitle={`${h.projects} projects · prepared by ${data.generated_by || "—"}`}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Input type="date" data-testid="client-period-start" value={period.start}
            onChange={(e) => setPeriod((p) => ({ ...p, start: e.target.value }))}
            className="w-[150px]" />
          <Input type="date" data-testid="client-period-end" value={period.end}
            onChange={(e) => setPeriod((p) => ({ ...p, end: e.target.value }))}
            className="w-[150px]" />
          <Button variant="outline" size="sm" data-testid="client-export-pdf"
            disabled={!!busy} onClick={() => download("pdf")} className="gap-1.5">
            {busy === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            PDF
          </Button>
          <Button variant="outline" size="sm" data-testid="client-export-xlsx"
            disabled={!!busy} onClick={() => download("xlsx")} className="gap-1.5">
            {busy === "xlsx" ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
            Excel
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-sm bg-slate-200 lg:grid-cols-6">
        {[
          ["cr-kpi-rag", "Overall", <RagBadge key="r" value={h.rag} />],
          ["cr-kpi-projects", "Projects", h.projects],
          ["cr-kpi-complete", "Average complete", `${h.avg_percent_complete}%`],
          ["cr-kpi-allocated", "Allocated", money(h.allocated_budget)],
          ["cr-kpi-change", "Approved change", money(data.commercial.approved_change_value)],
          ["cr-kpi-waiting", "Waiting on client", w.count],
        ].map(([testId, label, value]) => (
          <div key={testId} data-testid={testId} className="bg-white px-5 py-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</p>
            <p className="mt-1.5 truncate text-xl font-bold text-slate-900">{value}</p>
          </div>
        ))}
      </div>

      {data.movement && (
        <Section testId="cr-movement" title="Movement since the last pack"
          note={`Compared with the pack issued on ${String(data.movement.since).slice(0, 10)}`}>
          <div className="flex flex-wrap gap-4">
            {Object.entries(data.movement.changes).map(([k, v]) => {
              const Icon = v > 0 ? TrendingUp : v < 0 ? TrendingDown : Minus;
              const worse = ["red", "amber", "open_risks", "open_issues", "overdue_milestones"].includes(k);
              const tone = v === 0 ? "text-slate-500"
                : (v > 0) === worse ? "text-rose-700" : "text-emerald-700";
              return (
                <span key={k} data-testid={`cr-movement-${k}`}
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold ${tone}`}>
                  <Icon size={13} /> {k.replace(/_/g, " ")} {v > 0 ? `+${v}` : v}
                </span>
              );
            })}
          </div>
        </Section>
      )}

      <Section testId="cr-projects" title="Projects"
        note="The page most people read. One line per project.">
        <Table testId="cr-projects-table"
          head={["Project", "RAG", "Stage", "%", "Planned", "Forecast", "Slip", "Allocated", "Biggest risk"]}
          rows={data.projects.map((r) => [
            <span className="font-medium text-slate-900">{r.name}</span>,
            <RagBadge value={r.rag} />, r.stage || "—", `${r.percent_complete}%`,
            r.planned_finish || "—", r.forecast_finish || "—",
            r.slippage_days ? <span className="font-semibold text-rose-700">{r.slippage_days}d</span> : "—",
            money(r.allocated_budget), r.biggest_risk || "—",
          ])} />
      </Section>

      <Section testId="cr-delivery" title="Delivery performance"
        note={`${data.delivery.due_in_period} due in period · ${data.delivery.achieved_in_period} achieved · ${data.delivery.total_days_lost} days lost against baseline`}>
        <Table testId="cr-delivery-table"
          head={["Project", "Milestone", "Baseline", "Forecast", "Days lost", "Status"]}
          empty="No milestones have slipped against baseline."
          rows={data.delivery.slipped.map((r) => [
            r.project, r.title, r.baseline_date || "—", r.forecast_date || "—",
            <span className="font-semibold text-rose-700">{r.days_lost}</span>, r.status || "—",
          ])} />
      </Section>

      <Section testId="cr-commercial" title="Commercial position"
        note={`Contract ${money(data.commercial.contract_value)} · approved change ${money(data.commercial.approved_change_value)} and ${data.commercial.approved_change_days} days`}>
        <Table testId="cr-commercial-table"
          head={["Project", "Allocated", "Committed", "Variance", "Approved change"]}
          rows={data.commercial.by_project.map((r) => [
            r.project, money(r.allocated), money(r.committed), money(r.variance),
            money(r.approved_change),
          ])} />
      </Section>

      <Section testId="cr-exposure" title="Risk and issue exposure"
        note={`Open risks — ${e.risk_rag.Red} red, ${e.risk_rag.Amber} amber, ${e.risk_rag.Green} green · ${e.overdue_reviews.length} reviews past due`}>
        <Table testId="cr-risks-table"
          head={["Project", "Ref", "Risk", "Owner", "Score", "Mitigation"]}
          rows={e.top_risks.map((r) => [
            r.project, r.ref || "—", r.title, r.owner || "—",
            <span className="font-semibold tabular-nums">{r.score}</span>, r.mitigation || "—",
          ])} />
        {!!e.oldest_issues.length && (
          <div className="mt-5">
            <p className="field-label">Longest-running issues</p>
            <Table testId="cr-issues-table"
              head={["Project", "Issue", "Owner", "Severity", "Days open"]}
              rows={e.oldest_issues.map((r) => [
                r.project, r.title, r.owner || "—", r.severity || "—", r.days_open,
              ])} />
          </div>
        )}
      </Section>

      <Section testId="cr-waiting" title="Waiting on the client"
        note="Decisions and acceptances currently sitting with them, not with you.">
        <Table testId="cr-waiting-table" head={["Type", "Project", "Item", "Position"]}
          empty="Nothing is waiting on the client."
          rows={[
            ...w.pending_approvals.map((r) => [
              "Approval", r.project, r.title || "—", `${r.days_waiting} days waiting`]),
            ...w.dependencies.map((r) => [
              "Dependency", r.project, r.title || "—",
              r.overdue
                ? <span className="font-semibold text-rose-700">Overdue</span>
                : (r.due_date || "—")]),
            ...w.undecided_changes.map((r) => [
              "Change request", r.project, r.title || "—", "Awaiting decision"]),
          ]} />
      </Section>

      <Section testId="cr-activity" title="Decisions and changes in the period">
        <Table testId="cr-activity-table" head={["Type", "Project", "Item", "Status", "Cost", "Days"]}
          empty="Nothing was decided or changed in this period."
          rows={[
            ...data.period_activity.decisions.map((r) => [
              "Decision", r.project, r.title, r.status || "—", "—", "—"]),
            ...data.period_activity.changes.map((r) => [
              "Change", r.project, r.title, r.status || "—", money(r.cost_impact),
              r.schedule_impact_days ?? "—"]),
          ]} />
      </Section>

      <Section testId="cr-assurance" title="Assurance"
        note={data.assurance.summary
          ? `${data.assurance.summary.average_score}% average compliance`
          : undefined}>
        <Table testId="cr-assurance-table"
          head={["Project", "Framework", "Stage", "Gate", "Score", "Missing evidence"]}
          empty="No project here has a governance framework set, so there is nothing to measure yet."
          rows={data.assurance.rows.map((r) => [
            r.project, r.framework || "—", r.stage || "—", r.gate_status || "—", `${r.score}%`,
            r.missing.length
              ? <span className="text-rose-700">{r.missing.join(", ")}</span>
              : <span className="text-emerald-700">Complete</span>,
          ])} />
        {!!data.assurance.unassessed?.length && (
          <p data-testid="cr-assurance-unassessed" className="mt-3 text-xs text-slate-500">
            No governance framework is set on {data.assurance.unassessed.join(", ")} — these are
            unmeasured rather than non-compliant.
          </p>
        )}
      </Section>

      <Section testId="cr-benefits" title="Benefits">
        <Table testId="cr-benefits-table" head={["Project", "Benefit", "Category", "Target", "Status"]}
          empty="No benefits are being tracked for this client."
          rows={data.benefits.map((r) => [
            r.project, r.title, r.category || "—", r.target_value || "—", r.status || "—",
          ])} />
      </Section>

      <Section testId="cr-commentary" title="Commentary"
        note="Written once per client per period, and it appears at the top of the pack.">
        <Textarea data-testid="cr-commentary-input" rows={5} value={commentary}
          onChange={(ev) => setCommentary(ev.target.value)}
          placeholder="The headline for this period, in your own words." />
        <Button data-testid="cr-commentary-save" disabled={busy === "commentary"}
          onClick={saveCommentary} className="mt-3 gap-1.5"
          style={{ backgroundColor: "var(--brand-primary)" }}>
          {busy === "commentary" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save commentary
        </Button>
      </Section>
    </Shell>
  );
}
